import type { RenderOptions } from './Renderer';
import type { Renderer } from './Renderer';

export class RenderLoop {
  private renderer: Renderer;
  private running = false;
  private lastFrameTime = 0;
  private frameCallback: ((timestamp: number) => RenderOptions | null) | null = null;
  private rafId: number | null = null;
  private visibilityHandler: (() => void) | null = null;

  constructor(renderer: Renderer) {
    this.renderer = renderer;
  }

  start(): void {
    if (this.running) return;
    this.running = true;

    this.visibilityHandler = () => {
      if (document.hidden) {
        this.pauseLoop();
      } else {
        this.resumeLoop();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);

    this.lastFrameTime = 0;
    this.scheduleFrame();
  }

  stop(): void {
    this.running = false;

    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }

    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  isRunning(): boolean {
    return this.running;
  }

  setFrameCallback(callback: (timestamp: number) => RenderOptions | null): void {
    this.frameCallback = callback;
  }

  private scheduleFrame(): void {
    this.rafId = requestAnimationFrame((t) => this.frame(t));
  }

  private frame(timestamp: number): void {
    if (!this.running) return;

    if (timestamp - this.lastFrameTime < 16) {
      this.scheduleFrame();
      return;
    }
    this.lastFrameTime = timestamp;

    if (this.frameCallback) {
      const options = this.frameCallback(timestamp);
      if (options) {
        this.renderer.render(options);
      }
    }

    this.scheduleFrame();
  }

  private pauseLoop(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private resumeLoop(): void {
    if (!this.running) return;
    this.lastFrameTime = 0;
    this.scheduleFrame();
  }
}
